import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import useEventBrokerSubscription from '../common/helpers/useEventBrokerSubscription';
import { EventBroker } from '../../eventbroker';
import { ensureArray } from '../../util/js_utils';
import { withColorSchemeInfoStyled } from '../../redux/prefs/access';
import { useSidePanelLayout, useSidePanelPane } from './sidePanelContext';
import SidePanelResizeHandle from './SidePanelResizeHandle';

/**
 * Compute the set of pageIds that should be `open: true` after a side-panel
 * event. Pure function — extracted for testability per
 * gibbs-clarify-on-ambiguous-target's sister sidepanel study.
 *
 * Inputs:
 *   currentPages — array of { pageId, props.open, stackable, component.props.keepOpenOnZap }
 *   visiblePageId — the pageId requested by the event (or null if closing)
 *   eventData — the raw event data (used for zapAll detection)
 *
 * Behavior:
 *   - zapAll: keep only pages where props.open AND component.props.keepOpenOnZap
 *   - visiblePageId == null: empty set (close all)
 *   - requested page is stackable AND another stackable page is currently
 *     open (and is a different pageId): keep BOTH open
 *   - more than two stackable pages would be simultaneously open: warn,
 *     fall back to last-selected wins
 *   - otherwise: single-page-open (close all but the requested page)
 *
 * @returns {Set<string>}
 */
export function computeVisiblePageIds(currentPages, visiblePageId, eventData) {
    if (eventData && eventData.zapAll) {
        return new Set(
            currentPages
                .filter((p) => p.props && p.props.open
                    && p.component && p.component.props
                    && p.component.props.keepOpenOnZap)
                .map((p) => p.pageId),
        );
    }
    if (visiblePageId == null) {
        return new Set();
    }
    const requestedPage = currentPages.find((p) => p.pageId === visiblePageId);
    const requestedStackable = !!(requestedPage && requestedPage.stackable);
    const openStackableIds = currentPages
        .filter((p) => p.props && p.props.open && p.stackable && p.pageId !== visiblePageId)
        .map((p) => p.pageId);
    if (requestedStackable && openStackableIds.length > 0) {
        if (openStackableIds.length >= 2) {
            console.warn(
                '[SidePanel] More than two stackable pages requested simultaneously;'
                + ' falling back to last-selected wins.',
                { requested: visiblePageId, openStackable: openStackableIds },
            );
            return new Set([visiblePageId]);
        }
        return new Set([...openStackableIds, visiblePageId]);
    }
    return new Set([visiblePageId]);
}

// The open/close controls SidePanel injects into every long-lived page. Provided
// from the INITIAL render (not only after the first set<side>Panel event), so a
// long-lived panel that reacts to events before it has ever been opened — e.g.
// DockingPane closing itself on proteinsChanged — never calls an undefined setOpen.
//
// These must publish the same ADDRESSED close that handleSetEvents injects on
// every later render. A page closes itself; it does not close the column. The
// no-argument form is the column-level operation and belongs to
// `UserActions.CloseSidePanel`, not to a page.
function panelControlProps(side, pageId) {
    const handleClose = () => EventBroker.publish(`set${side}Panel`, { pageId, close: true });
    return {
        handleClose,
        setOpen(open, props) {
            if (!open) {
                handleClose();
            } else {
                EventBroker.publish(`set${side}Panel`, { pageId, props: { ...props } });
            }
        },
    };
}

/**
 * SidePanel - class to manage side panels
 * Content panels can be temporary or long-lived. Temporary panels are unmounted when closed.
 * Long-lived panels are cloned when displayed, but preserve their state variables across clones.
 *
 * Pages can opt into "stackable" mode by setting `stackable: true` as a prop on the
 * child component. Two stackable pages can be open at the same time inside the column,
 * stacked vertically (declared order: first child on top). Non-stackable pages preserve
 * the existing single-page-open behavior — opening a non-stackable page closes any
 * stackable pages that were open. See bmaps-sidepanel-co-display-and-resize study.
 *
 * side: `left` | `right`
 * The "default" page is optional - what will be displayed when the panel is "closed"
 * For example, the Selector in InfoDisplay
 *
 * @param {{
 *      side: string,
 *      defaultPage: { pageId: string, component: JSX.Element, props: object }?,
 *      defaultPageId: string?,
 *      children: JSX.Element[]?,
 * }} props
 *
 * @example `UserActions.OpenSidePanel('left', { pageId: 'docking' });`
 * @example `Eventbroker.publish(setInfoPanel, <jsx />)
*/
export default function SidePanel({
    side, defaultPage, defaultPageId, children,
}) {
    // pageInfoList - currently mounted pages
    // This can be initially populated with JSX children or a default page object.
    const [pageInfoList, setPageInfoList] = useState(() => {
        const pages = [];
        if (defaultPage) {
            pages.push({
                pageId: defaultPage.pageId,
                component: defaultPage.component,
                props: {
                    ...defaultPage.props,
                    open: true,
                    ...panelControlProps(side, defaultPage.pageId),
                },
                longLived: true,
                stackable: !!defaultPage.props?.stackable,
            });
        }
        if (children) {
            for (const child of ensureArray(children)) {
                pages.push({
                    pageId: child.props.pageId,
                    component: child,
                    props: {
                        ...child.props,
                        open: false,
                        ...panelControlProps(side, child.props.pageId),
                    },
                    longLived: true,
                    stackable: !!child.props.stackable,
                });
            }
        }
        return pages;
    });

    // Resize needs to run after rendering, so needs to be in useEffect
    useEffect(() => {
        EventBroker.publish('resize');
    }, [pageInfoList]);

    // TODO: consider adding an 'op' field to the data: open, toggle, close, zapAll, etc...
    const handleZapAll = useCallback(() => { EventBroker.publish(`set${side}Panel`, { zapAll: true }); }, [side]);
    const handleSetEvents = useCallback((_, data) => {
        // There's a request to update the SidePanel
        // Possibilities:
        // 1. Close / reset to default, by passing no arguments
        // 2. Display an existing page, by passing a pageId, perhaps with optional props to update
        // 3. Add and display a new page, by passing a pageId, component, and optional props
        // 4. Toggle an existing page, by passing a pageId and toggle: true
        // 5. Close a specific page, by passing a pageId and close: true
        // The `longLived` option controls whether to keep the page mounted when it's not visible.

        // The page this event is about, as it is mounted right now. Look it up BEFORE the
        // temporary pages are removed below: a temporary page (longLived: false, e.g. the
        // Screening panel) is only in this list, so judging a close or toggle by the filtered
        // list treats an open temporary page as "not open" and ignores its X button.
        const currentPage = pageInfoList.find((page) => page.pageId === data?.pageId);

        // Remove temporary pages
        let updatedPages = pageInfoList.filter((page) => page.longLived);
        // Add new page if it doesn't exist. Only an event that carries a component can add
        // one; a close or toggle addressed to a temporary page has none, and it is removed
        // (not re-added) by the filter above.
        const existingPage = updatedPages.find((page) => page.pageId === data?.pageId);
        if (data?.pageId && !existingPage && data.component) {
            updatedPages.push(data);
        }

        const fallbackId = defaultPageId || defaultPage?.pageId;

        // Explicit close of a not-open page is a no-op
        if (data?.close && !currentPage?.props.open) return;

        // Per-page close intent: X click, programmatic close({pageId}), or
        // toggle of an already-open page. These must close that specific
        // page WITHOUT re-stacking the default page on top of any other
        // stackable page that's still open.
        const isPerPageClose = !!(data?.pageId && currentPage?.props.open
            && (data.toggle || data.close));

        let visiblePageIds;
        if (isPerPageClose) {
            visiblePageIds = new Set(
                updatedPages
                    .filter((p) => p.props.open && p.pageId !== data.pageId)
                    .map((p) => p.pageId),
            );
            // 2026-06-01: do NOT fall back to fallbackId on empty here.
            // An explicit per-page close (X button or toggle-on-open) should
            // fully close the SidePanel column when the last open pane is the
            // one being closed. Earlier this branch resurrected the default
            // page, making the X button feel broken — clicking X on Help-only
            // re-opened Help immediately. The fallback path is still active
            // for *non-close* paths (else branch below), so a setrightPanel
            // with no data still resolves to the default page as designed.
        } else {
            const requestedPageId = data?.pageId;
            const visiblePageId = requestedPageId || fallbackId;
            // Stacking rule lives in computeVisiblePageIds (pure function for
            // testability — see test/gibbs/ui/sidePanelPageInfoList.test.js).
            visiblePageIds = computeVisiblePageIds(updatedPages, visiblePageId, data);
        }

        // Inject props into page components:
        // * open, setopen, handleClose, longLived
        // * and maybe other incoming props for the target page
        updatedPages = updatedPages.map((pageInfo) => ({
            ...pageInfo,
            props: {
                ...pageInfo.props,
                ...((pageInfo.pageId === data?.pageId) ? (data?.props) : {}),
                open: visiblePageIds.has(pageInfo.pageId),
                setOpen(open, props) {
                    if (!open) {
                        // Closing is addressed to THIS page, never to "whatever is
                        // open". A long-lived page can call setOpen(false) in reaction
                        // to an event unrelated to its own visibility (e.g. DockingPane's
                        // handleProteinsChanged closes itself whenever the loaded
                        // proteins change, whether or not docking is even open) - and a
                        // close with no notion of "which page asked" would take down
                        // whichever OTHER page happens to be open (#1065: it was closing
                        // the Screening side panel out from under an in-progress
                        // structure load). handleSetEvents no-ops a close aimed at a page
                        // that isn't open, which is what makes that safe.
                        EventBroker.publish(`set${side}Panel`, { pageId: pageInfo.pageId, close: true });
                    } else {
                        EventBroker.publish(`set${side}Panel`, { pageId: pageInfo.pageId, props: { ...props } });
                    }
                },
                handleClose: () => EventBroker.publish(
                    `set${side}Panel`,
                    { pageId: pageInfo.pageId, close: true },
                ),
                longLived: pageInfo.longLived,
            },
        }));
        setPageInfoList(updatedPages);
    }, [pageInfoList, defaultPageId, defaultPage?.pageId, side]);

    useEventBrokerSubscription(`set${side}Panel`, handleSetEvents);
    useEventBrokerSubscription('zapAll', handleZapAll);

    // Count open pages to know whether we're in stacked mode. When stacked,
    // wrap children in a flex column and pass `isStacked: true` so each
    // SidePanelContainer applies the multi-page sizing rules.
    const openCount = pageInfoList.filter((p) => p.props.open).length;
    const isStacked = openCount > 1;

    // 2026-06-01: per-pane drag dispatchers from the layout context.
    // Each non-first open stackable pane gets its own resize handle on its
    // inboard edge. The outermost open pane is still resized by the
    // column-level handle that lives in the layout component. Always call
    // the hook (rules-of-hooks); a SidePanel not wrapped in a layout sees
    // the default context, and `inLayout` is what tells it so — the
    // dispatchers would otherwise be no-ops driving visible handles.
    const ctxRaw = useSidePanelLayout();
    const ctx = ctxRaw.inLayout ? ctxRaw : null;

    // Tell the layout which pages are open, so it does not work that out a second time.
    // From an effect, not during render, because this writes state in the parent. Keyed
    // on the joined ids so an unchanged set never calls; the layout also compares by
    // value, so neither direction can loop.
    // `|` is a safe separator: page ids are identifiers declared in JSX, never free text.
    const openPageIdsKey = pageInfoList.filter((p) => p.props.open).map((p) => p.pageId).join('|');
    const reportOpenPages = ctx && ctx.reportOpenPages;
    useEffect(() => {
        if (!reportOpenPages) return;
        reportOpenPages(openPageIdsKey === '' ? [] : openPageIdsKey.split('|'));
    }, [reportOpenPages, openPageIdsKey]);

    // The resize handle sits on the pane's INBOARD edge: a right-mounted
    // column grows leftward, a left-mounted one rightward.
    const handleEdge = side === 'right' ? 'left' : 'right';

    // Track which open stackable pane is the FIRST one (no per-pane
    // handle for it — the column-level handle handles it).
    let firstOpenStackableSeen = false;

    const inner = pageInfoList.map((pageInfo) => {
        // cloneElement is used because we need to pass `open` prop into the component.
        // Note: useState variables are maintained in the clone.
        // For example, the DockingPane has a dockingState useState. The state is
        // preserved across several calls to setSidePanel, because the useState hook
        // implementation returns consistent values and setter functions in the clones.
        const cloned = React.cloneElement(pageInfo.component, {
            ...pageInfo.props,
            isStacked,
        });
        // Decide whether to render a per-pane handle just left of this
        // pane. Stacked mode only, this pane must be open, and there
        // must already be at least one earlier open stackable pane.
        const isOpenStackable = isStacked
            && pageInfo.props.open
            && pageInfo.props.stackable !== false;
        const renderPerPaneHandle = isOpenStackable && firstOpenStackableSeen && ctx;
        if (isOpenStackable) firstOpenStackableSeen = true;

        return (
            <React.Fragment key={pageInfo.pageId}>
                {renderPerPaneHandle && (
                    <SidePanelResizeHandle
                        side={handleEdge}
                        disabled={ctx.handleDisabled}
                        onDragStart={() => ctx.beginPaneDrag(pageInfo.pageId)}
                        onWidthChange={(w) => ctx.updatePaneWidth(pageInfo.pageId, w)}
                        onDragEnd={() => ctx.endPaneDrag(pageInfo.pageId)}
                    />
                )}
                {cloned}
            </React.Fragment>
        );
    });

    // 2026-06-02: ALWAYS render a div wrapper here, even in non-stacked
    // mode. Previously the non-stacked branch returned `<>{inner}</>`
    // (Fragment) while the stacked branch returned `<div>{inner}</div>`.
    // When isStacked transitioned (a second stackable pane opened or
    // closed), the parent type at the same tree position changed from
    // Fragment → div (or vice versa). React's reconciliation treats
    // type changes at the same position as an unmount+remount —
    // including unmounting HelpPanel, which dropped ChatBot's local
    // useState message history.
    //
    // User-visible symptom: clicking a target pill in a clarify card
    // (which opens Workflow Checker via dispatchResumedPlan →
    // workflowProposed → setOpen(true)) wiped the chat history because
    // the layout swap triggered the remount cascade. Now the wrapper
    // is constant; only its CSS varies.
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'row',
            height: '100%',
            minWidth: 0,
            alignItems: 'stretch',
        }}
        >
            {inner}
        </div>
    );
}

// SidePanelContainer wraps a styled component so a side panel column's dynamic
// width flows into every page automatically. An explicit `width` prop still
// takes precedence, which preserves behavior for left-mounted panels and
// standalone uses (e.g., CompoundTableSidePanel).
//
// **Pane-aware width resolution.** A stackable pane passes `paneId` and its
// width resolves from that pane's slice via `useSidePanelPane(paneId)`. The
// panes that pass it are `HelpPanel` and `WorkflowPane`, which arrive in later
// slices — until then every consumer takes the column-level or default width.
// (The fork's comment credits `<PaneColumn>` with setting `paneId`; that is
// wrong even there — PaneColumn is rendered nowhere, and was not migrated.
// See docs/gibbs-migration-register.md.)
//
// Resolution order: explicit prop → pane-scoped → column ctx → default.
export const DEFAULT_SIDEPANEL_WIDTH = '18rem';

export function SidePanelContainer(props) {
    const { width: explicitWidth, paneId, isStacked } = props;
    const ctx = useSidePanelLayout();
    // Always call the hook — it's a no-op (returns defaults) when
    // paneId is undefined or the pane is not registered. Calling
    // unconditionally satisfies React's rules-of-hooks.
    const paneState = useSidePanelPane(paneId);
    const paneWidth = paneId ? paneState.width : null;
    // `!= null`, not `||`: width={0} is a real value here. The column hides
    // itself in rail mode with width 0 + visibility:hidden, and `||` would
    // send that through to the default instead of honouring it.
    let width = explicitWidth;
    if (width == null && paneWidth != null) width = `${paneWidth}px`;
    if (width == null) width = ctx.width;
    if (width == null) width = DEFAULT_SIDEPANEL_WIDTH;
    // `$isStacked`: styled-components' transient-prop convention, which this
    // repo uses throughout (see `$colorSchemeInfo` below, and 19 other files).
    // The `$` is what keeps it off the DOM - no shouldForwardProp needed.
    return <SidePanelContainerStyled {...props} width={width} $isStacked={isStacked} />;
}

const SidePanelContainerStyled = withColorSchemeInfoStyled(styled(({
    title, children, className, width, containerStyle,
    // The following are inserted by SidePanel, and passed through by parent Component.
    open, handleClose, longLived, contentStyle,
    // SidePanel sets `isStacked` when multiple stackable pages are open at once;
    // the wrapper above forwards it as the transient `$isStacked`, which the
    // styled template below reads. When true the container becomes a flex item
    // sharing the column's height instead of claiming all of it.
}) => (
    !!(open || longLived) && (
        <div className={className} style={{ display: open ? 'flex' : 'none' }}>
            <div className="sidepanel-container" style={{ width, ...containerStyle }}>
                <div className="sidepanel-container-title">
                    <div>{title}</div>
                    <button type="button" onClick={() => handleClose()}>
                        <i className="fa fa-close" />
                    </button>
                </div>
                <div className="sidepanel-container-content" style={contentStyle}>
                    {children}
                </div>
            </div>
        </div>
    )
))`
${({ $colorSchemeInfo: { css: color }, $isStacked }) => `
position: relative;
${$isStacked
    /* 2026-06-01: switched from `flex: 1 1 0` (equal-share growth) to
       `flex: 0 0 auto` so each stacked pane respects its inline width
       (set on the inner .sidepanel-container by paneWidth). Earlier
       the equal-share rule was forcing both panes to claim half the
       row regardless of their persisted widths, which made the
       per-pane resize feel unresponsive and left mystery "empty
       space" wherever a pane's content was narrower than its share. */
    ? 'flex: 0 0 auto; min-width: 0; min-height: 0;'
    : 'height: 100%;'}
& .sidepanel-container {
    background: ${color};
    display: flex;
    flex-direction: column;
    /* 2026-06-01: CSS containment + compositor-layer promotion to
       keep style/layout/paint scoped within the pane during resize
       drags. Containment alone took FPS from 0 → 13 but the wide
       DOM was still re-laying out per frame. Adding will-change +
       transform: translateZ(0) promotes the pane to its own GPU
       layer, so paints don't ripple through the rest of the page. */
    contain: layout style paint;
    will-change: width;
    transform: translateZ(0);
    z-index: 1;
    ${$isStacked ? 'height: 100%;' : ''}
    /* 2026-06-01: removed the 200ms width transition. With the
       ghost-line drag pattern in SidePanelResizeHandle, width
       changes happen exactly once (on mouseup) and should snap to
       the cursor's final position rather than animating into place.
       The transition also caused a perceptible jank on drop when
       isResizing flipped to false in the same render as the width
       commit. Other width changes (open/close, expand-from-rail)
       are intentional state transitions that read fine as instant
       too. */
}
& .sidepanel-container-content {
    margin: .2rem .5rem;
    overflow: auto;
    border-radius: 0 0 .8rem .8rem;
    flex: 1;
    background: white;
    border: 1px solid black;
    color: black;
    box-shadow: inset 0 -2px 4px black;
    display: flex;
    flex-direction: column;
}
& .sidepanel-container-title {
    margin: .5rem .5rem 0 .5rem;
    display: flex;
    justify-content: center;
    padding: .5rem;
    border-radius: .8rem .8rem 0 0;
    font-size: 2.5vmin;
    font-family: sans-serif;
    font-weight: bold;
    background: white;
    border: 1px solid black;
    color: black;
    box-shadow: inset 0 1px 4px black;
}
& .sidepanel-container-title button {
    fontSize: inherit;
    background-color: transparent;
    color: inherit;
    border: none;
    margin: 0;
    margin-left: auto;
    text-align: inherit;
    outline: none;
}
& .sidepanel-container-title div {
    margin-left: auto;
}
@media only screen and (max-width: 800px) {
    & .sidepanel-container-content{
        position: absolute;
        bottom: 0;
        top: calc(var(--base-font-size) + 1rem);
        left:0;
        right: 0;
        margin: 0rem;
        border-radius: 0;
    }
    & .sidepanel-container-title{
        position: absolute;
        top:0;
        left:0;
        right: 0;
        margin: 0rem;
        border-radius: 0;
    }
}
`}
// Close two template literals: inner for CSS with colors added; outer for styled-component input
`);

export function SidePanelSection({ children }) {
    return (
        <div style={{
            background: 'white',
            color: 'black',
            borderRadius: '.2rem',
            padding: '.5rem',
        }}
        >
            {children}
        </div>
    );
}
